/**
 * Slate File I/O
 * Read and write .slate files
 */

import JSZip from 'jszip';
import type {
  SlateFile,
  SlateManifest,
  SlateDesign,
} from '@/types/slate';
import {
  SLATE_VERSION,
  SLATE_EXTENSION,
  createManifest,
  createDesignFromTemplate,
  isValidManifest,
  isValidDesign,
  isCompatibleVersion,
} from '@/types/slate';
import type { Template } from '@/types/template';
import type { AdFormat } from '@/types/formats';

/**
 * Export options
 */
export interface ExportOptions {
  /** Include preview image */
  includePreview?: boolean;
  /** Preview scale (1 = actual size) */
  previewScale?: number;
  /** Custom name for the file */
  name?: string;
  /** Description */
  description?: string;
  /** Tags */
  tags?: string[];
}

/**
 * Create a .slate file from template and format
 */
export async function createSlateFile(
  template: Template,
  format: AdFormat,
  variableValues: Record<string, string>,
  previewBlob?: Blob,
  options: ExportOptions = {}
): Promise<Blob> {
  const zip = new JSZip();

  // Create manifest
  const manifest = createManifest(options.name || template.metadata.name, {
    description: options.description || template.metadata.description,
    tags: options.tags || template.metadata.tags,
    preview: previewBlob
      ? {
          width: format.width,
          height: format.height,
          format: 'png',
        }
      : undefined,
  });

  // Create design
  const design = createDesignFromTemplate(template, format, variableValues);

  // Add manifest
  zip.file('manifest.json', JSON.stringify(manifest, null, 2));

  // Add design
  zip.file('design.json', JSON.stringify(design, null, 2));

  // Add preview if provided
  if (previewBlob) {
    zip.file('preview.png', previewBlob);
  }

  // Create assets folder (even if empty, for structure)
  zip.folder('assets');

  // Generate the zip
  return zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 6 },
  });
}

/**
 * Read a .slate file
 */
export async function readSlateFile(file: File | Blob): Promise<SlateFile> {
  const zip = await JSZip.loadAsync(file);

  // Read manifest
  const manifestFile = zip.file('manifest.json');
  if (!manifestFile) {
    throw new Error('Invalid .slate file: missing manifest.json');
  }
  const manifestText = await manifestFile.async('string');
  const manifest = JSON.parse(manifestText);

  if (!isValidManifest(manifest)) {
    throw new Error('Invalid .slate file: malformed manifest');
  }

  // Check version compatibility
  if (!isCompatibleVersion(manifest.version)) {
    throw new Error(
      `Incompatible .slate version: ${manifest.version} (expected ${SLATE_VERSION})`
    );
  }

  // Read design
  const designFile = zip.file('design.json');
  if (!designFile) {
    throw new Error('Invalid .slate file: missing design.json');
  }
  const designText = await designFile.async('string');
  const design = JSON.parse(designText);

  if (!isValidDesign(design)) {
    throw new Error('Invalid .slate file: malformed design');
  }

  // Read preview if exists
  let preview: Blob | undefined;
  const previewFile = zip.file('preview.png');
  if (previewFile) {
    preview = await previewFile.async('blob');
  }

  // Read assets
  const assets = new Map<string, Blob>();
  const assetsFolder = zip.folder('assets');
  if (assetsFolder) {
    const assetFiles = assetsFolder.filter((_, file) => !file.dir);
    for (const assetFile of assetFiles) {
      const blob = await assetFile.async('blob');
      assets.set(assetFile.name.replace('assets/', ''), blob);
    }
  }

  return {
    manifest,
    design,
    preview,
    assets,
  };
}

/**
 * Download a .slate file
 */
export function downloadSlateFile(blob: Blob, filename: string): void {
  // Ensure proper extension
  const name = filename.endsWith(SLATE_EXTENSION)
    ? filename
    : `${filename}${SLATE_EXTENSION}`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generate a filename for a slate file
 */
export function generateSlateFilename(
  templateName: string,
  formatId: string
): string {
  const sanitized = templateName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const timestamp = Date.now();
  return `${sanitized}-${formatId}-${timestamp}`;
}

/**
 * Capture canvas preview as blob
 */
export async function captureCanvasPreview(
  canvasElement: HTMLElement,
  scale: number = 1
): Promise<Blob> {
  const html2canvas = (await import('html2canvas')).default;
  const canvas = await html2canvas(canvasElement, {
    scale,
    backgroundColor: null,
    logging: false,
    useCORS: true,
  });

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to capture canvas'));
        }
      },
      'image/png'
    );
  });
}

// Re-export types and utilities
export * from '@/types/slate';
